/**
 * Pairs & pairing (brief §5 Phase 3, §4D).
 *
 * The pairing confirmation screen: confident matches are proposed, the
 * operator confirms (optionally swapping primary/backup) or denies, and
 * decisions are remembered server-side — a denied pair is never re-asked.
 * Also: the pairs table with drift state + write-to-nodes, the backup
 * rollout for unpaired extensions (ADR 0029), and the dial-plan rulesets.
 *
 * Every action here reports what it did. These runs are frequently no-ops
 * by design (nothing new to propose, no drift), and a silent no-op is
 * indistinguishable from a broken button — which is exactly how a stale
 * inventory hid itself.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  checkPairDrift,
  confirmSuggestion,
  denySuggestion,
  getInventoryStatus,
  getPairs,
  getRulesets,
  getSuggestions,
  getUnpaired,
  pushDialplan,
  refreshInventory,
  refreshPairing,
  writePair,
} from "@pbxmgr/api";
import type { PairingSuggestion } from "@pbxmgr/api";
import { Alert, Badge, Button, Spinner } from "@pbxmgr/ui";
import type { BadgeTone } from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";
import { BackupRolloutPanel, ServiceStandbyPanel } from "./BackupRollout";
import { DriftFlagsPanel } from "./DriftFlags";
import { Panel } from "../dashboard/panels";

const PAIR_TONE: Record<string, BadgeTone> = {
  ok: "online",
  verified: "online",
  drifted: "degraded",
  pending: "unknown",
  error: "offline",
};

/** Render a run summary ("3 proposed, 12 known") from its count map. */
function summarize(result: Record<string, number>): string {
  const parts = Object.entries(result)
    .filter(([, n]) => typeof n === "number")
    .map(([k, n]) => `${n} ${k}`);
  return parts.length > 0 ? parts.join(", ") : "no changes";
}

function errorText(err: unknown): string {
  return err instanceof ApiError ? (err.detail ?? err.message) : "request failed";
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function SuggestionRow({
  s,
  canManage,
  onDone,
}: {
  s: PairingSuggestion;
  canManage: boolean;
  onDone: () => void;
}): ReactNode {
  const confirm = useMutation({
    mutationFn: (swap: boolean) => confirmSuggestion(s.id, swap),
    onSuccess: onDone,
  });
  const deny = useMutation({ mutationFn: () => denySuggestion(s.id), onSuccess: onDone });
  const busy = confirm.isPending || deny.isPending;

  return (
    <tr>
      <td>
        node {s.primary_node_id} · <strong>{s.primary_ext}</strong>
      </td>
      <td>
        node {s.backup_node_id} · <strong>{s.backup_ext}</strong>
      </td>
      <td>{s.matched_on ?? "—"}</td>
      <td>
        {canManage ? (
          <div className="pairing-actions">
            <Button disabled={busy} onClick={() => confirm.mutate(false)}>
              Confirm
            </Button>
            <Button variant="secondary" disabled={busy} onClick={() => confirm.mutate(true)}>
              Confirm swapped
            </Button>
            <Button variant="secondary" disabled={busy} onClick={() => deny.mutate()}>
              Deny
            </Button>
          </div>
        ) : (
          <span className="ui-empty">read-only</span>
        )}
        {(confirm.isError || deny.isError) && <Alert>Action failed — check the audit log.</Alert>}
      </td>
    </tr>
  );
}

export function PairingPage(): ReactNode {
  const canManage = usePermission("extensions.manage");
  const canDialplan = usePermission("dialplan.manage");
  const qc = useQueryClient();
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["topo"] });
  };

  const proposed = useQuery({
    queryKey: ["topo", "suggestions", "proposed"],
    queryFn: () => getSuggestions("proposed"),
  });
  const denied = useQuery({
    queryKey: ["topo", "suggestions", "denied"],
    queryFn: () => getSuggestions("denied"),
  });
  const pairs = useQuery({ queryKey: ["topo", "pairs"], queryFn: getPairs });
  const unpaired = useQuery({ queryKey: ["topo", "unpaired"], queryFn: getUnpaired });
  const rulesets = useQuery({ queryKey: ["topo", "rulesets"], queryFn: getRulesets, enabled: canDialplan });

  const inventory = useQuery({ queryKey: ["topo", "inventory"], queryFn: getInventoryStatus });

  // One shared line reports whatever ran last. Previously these mutations
  // discarded their result and had no error handler, so a 0-change run, a
  // 12-change run, and a 500 all looked identical: nothing happened.
  const [outcome, setOutcome] = useState<string | null>(null);
  const [outcomeError, setOutcomeError] = useState<string | null>(null);
  const report =
    (label: string) =>
    (result: Record<string, number>): void => {
      setOutcomeError(null);
      setOutcome(`${label}: ${summarize(result)}.`);
      invalidate();
    };
  const reportError = (err: unknown): void => {
    setOutcome(null);
    setOutcomeError(errorText(err));
  };

  const reimport = useMutation({
    mutationFn: refreshInventory,
    onSuccess: report("Inventory refreshed"),
    onError: reportError,
  });
  const refresh = useMutation({
    mutationFn: refreshPairing,
    onSuccess: report("Pair proposals"),
    onError: reportError,
  });
  const drift = useMutation({
    mutationFn: checkPairDrift,
    onSuccess: report("Drift check"),
    onError: reportError,
  });
  const push = useMutation({ mutationFn: pushDialplan, onSuccess: invalidate });
  const write = useMutation({ mutationFn: writePair, onSuccess: invalidate });

  return (
    <div>
      <div className="dash__head">
        <h1>Pairs &amp; pairing</h1>
        {canManage ? (
          <div className="dash__status">
            <Button
              variant="secondary"
              disabled={reimport.isPending}
              onClick={() => reimport.mutate()}
            >
              {reimport.isPending ? "Refreshing…" : "Refresh inventory"}
            </Button>
            <Button disabled={refresh.isPending} onClick={() => refresh.mutate()}>
              {refresh.isPending ? "Proposing…" : "Propose pairs"}
            </Button>
            <Button variant="secondary" disabled={drift.isPending} onClick={() => drift.mutate()}>
              {drift.isPending ? "Checking…" : "Check drift"}
            </Button>
          </div>
        ) : null}
      </div>

      {outcome ? <p className="pairing-outcome">{outcome}</p> : null}
      {outcomeError ? <Alert>{outcomeError}</Alert> : null}

      <Panel title="Account inventory">
        <p>
          Pair proposals are planned from this inventory, and a pair needs the same person on
          two different nodes — so a node that has never imported can only ever yield zero
          proposals. The importer re-runs hourly; “Refresh inventory” runs it now.
        </p>
        {inventory.isLoading ? (
          <Spinner />
        ) : (
          <table className="ui-table">
            <thead>
              <tr>
                <th>Node</th>
                <th>Accounts</th>
                <th>Last import</th>
              </tr>
            </thead>
            <tbody>
              {inventory.data?.items.map((row) => (
                <tr key={row.node_id}>
                  <td>{row.node}</td>
                  <td>{row.accounts}</td>
                  <td>
                    {row.last_import ? (
                      formatWhen(row.last_import)
                    ) : (
                      <Badge tone="offline">never imported</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title={`Proposed pairs (${proposed.data?.total ?? 0})`}>
        {proposed.isLoading ? (
          <Spinner />
        ) : (proposed.data?.items.length ?? 0) === 0 ? (
          <div className="ui-empty">
            Nothing awaiting confirmation. “Propose pairs” scans the inventory for new confident
            matches; decided pairs are never re-asked.
          </div>
        ) : (
          <table className="ui-table">
            <thead>
              <tr>
                <th>Primary</th>
                <th>Backup</th>
                <th>Matched on</th>
                <th>Decision</th>
              </tr>
            </thead>
            <tbody>
              {proposed.data?.items.map((s) => (
                <SuggestionRow key={s.id} s={s} canManage={canManage} onDone={invalidate} />
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title={`Pairs (${pairs.data?.total ?? 0})`}>
        {(pairs.data?.items.length ?? 0) === 0 ? (
          <div className="ui-empty">No pairs yet.</div>
        ) : (
          <table className="ui-table">
            <thead>
              <tr>
                <th>Primary</th>
                <th>Backup</th>
                <th>Publicized</th>
                <th>State</th>
                <th>Last verified</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pairs.data?.items.map((p) => (
                <tr key={p.id}>
                  <td>
                    node {p.primary_node_id} · <strong>{p.primary_ext}</strong>
                  </td>
                  <td>
                    node {p.backup_node_id} · <strong>{p.backup_ext}</strong>
                  </td>
                  <td>{p.publicized_number ?? "—"}</td>
                  <td>
                    <Badge tone={PAIR_TONE[p.state] ?? "unknown"}>{p.state}</Badge>
                  </td>
                  <td>{formatWhen(p.last_verified)}</td>
                  <td>
                    {canManage ? (
                      <Button
                        variant="secondary"
                        disabled={write.isPending}
                        onClick={() => write.mutate(p.id)}
                      >
                        Write to nodes
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {write.isError ? <Alert>Pair write failed — see node status / audit log.</Alert> : null}
      </Panel>

      {/* Unpaired extensions are now actionable: BackupRolloutPanel offers a
          target for each and creates the missing accounts (ADR 0029). The
          table below stays as the read-only detail view. */}
      <DriftFlagsPanel />

      <BackupRolloutPanel />

      <ServiceStandbyPanel />

      <Panel title={`Unpaired account details (${unpaired.data?.total ?? 0})`}>
        {(unpaired.data?.items.length ?? 0) === 0 ? (
          <div className="ui-empty">Every discovered extension is paired.</div>
        ) : (
          <table className="ui-table">
            <thead>
              <tr>
                <th>Node</th>
                <th>Extension</th>
                <th>Name</th>
                <th>Email</th>
                <th>Department</th>
              </tr>
            </thead>
            <tbody>
              {unpaired.data?.items.map((a) => (
                <tr key={a.id}>
                  <td>{a.source_node_id}</td>
                  <td>{a.account_name}</td>
                  <td>{a.display_name ?? "—"}</td>
                  <td>{a.email ?? "—"}</td>
                  <td>{a.department ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {canDialplan ? (
        <Panel title="Dial plans">
          <div className="pairing-actions" style={{ marginBottom: "var(--space-3)" }}>
            <Button disabled={push.isPending} onClick={() => push.mutate()}>
              Generate + push to all nodes
            </Button>
          </div>
          {(rulesets.data?.items.length ?? 0) === 0 ? (
            <div className="ui-empty">No rulesets pushed yet.</div>
          ) : (
            <table className="ui-table">
              <thead>
                <tr>
                  <th>Node</th>
                  <th>Rules</th>
                  <th>Last pushed</th>
                  <th>Drift</th>
                </tr>
              </thead>
              <tbody>
                {rulesets.data?.items.map((r) => (
                  <tr key={r.node_id}>
                    <td>{r.node_id}</td>
                    <td>{r.rule_count}</td>
                    <td>{formatWhen(r.last_pushed)}</td>
                    <td>
                      <Badge tone={r.drift_state === "ok" ? "online" : "degraded"}>
                        {r.drift_state ?? "unknown"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {push.isError ? <Alert>Dial-plan push failed.</Alert> : null}
        </Panel>
      ) : null}

      <Panel title={`Denied (remembered — never re-asked) (${denied.data?.total ?? 0})`}>
        {(denied.data?.items.length ?? 0) === 0 ? (
          <div className="ui-empty">No denied suggestions.</div>
        ) : (
          <table className="ui-table">
            <thead>
              <tr>
                <th>Primary</th>
                <th>Backup</th>
                <th>Matched on</th>
                <th>Decided by</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {denied.data?.items.map((s) => (
                <tr key={s.id}>
                  <td>
                    node {s.primary_node_id} · {s.primary_ext}
                  </td>
                  <td>
                    node {s.backup_node_id} · {s.backup_ext}
                  </td>
                  <td>{s.matched_on ?? "—"}</td>
                  <td>{s.decided_by ?? "—"}</td>
                  <td>{formatWhen(s.decided_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
